import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import UserProfile from '../index';
import Button from '../../../components/ui/Button';
import AppIcon from '../../../components/AppIcon';
import SkillsSection from './components/SkillsSection';
import StrengthsSection from './components/StrengthsSection';
import AddSkillModal from './components/AddSkillModal';
import SuggestSkillModal from './components/SuggestSkillModal';
import SkillStrengthDetailDrawer from './components/SkillStrengthDetailDrawer';
import { getUserSkills, addUserSkill, editUserSkill } from '../../../api/Employee/skills';
import { UserSkillView, UserSkillInput, ProficiencyLevel } from '../../../models/employee';
import { Roles } from '../../../components/roles';
import { RootState } from '../../../store/redux/store/store';

interface Strength {
  id: string;
  name: string;
  confidence: string;
  confirmedBy: string;
  confirmedDate: string;
  evidenceSummary: string;
  evidenceCount: number;
  sources: { type: string; count: number }[];
}

const Skills: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const role = useSelector((state: RootState) => state.user.role);
  const userId = useSelector((state: RootState) => state.user.userId);
  const selectedUser = useSelector((state: RootState) => state.user.selectedUser);
  const selectedUserManagers = useSelector((state: RootState) => state.user.selectedUserManagers);
  const subjectUserId = id ?? userId;

  const isViewingAnotherUser = !!id && id !== userId;
  const isHr = Roles.HR.includes(role);
  const isManagerOfEmployee = (selectedUserManagers ?? []).some(
    (s: any) => String(s?.id) === String(userId)
  );

  // Determine user role for permissions
  const userRole: 'employee' | 'manager' = (isHr || isManagerOfEmployee) && isViewingAnotherUser ? 'manager' : 'employee';

  const [showAddSkillModal, setShowAddSkillModal] = useState(false);
  const [showSuggestSkillModal, setShowSuggestSkillModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<{ item: UserSkillView | Strength | null; type: 'skill' | 'strength' }>({
    item: null,
    type: 'skill'
  });

  // Skills data from API
  const [skills, setSkills] = useState<UserSkillView[]>([]);

  // Strengths data (to be implemented with actual API later)
  const [strengths] = useState<Strength[]>([]);

  useEffect(() => {
    if (subjectUserId) {
      loadSkills();
    }
  }, [subjectUserId]);

  const loadSkills = async () => {
    if (!subjectUserId) return;
    setLoading(true);
    try {
      const result = await getUserSkills(subjectUserId);
      if (result.status === 200 && result.payload) {
        setSkills(result.payload);
      }
    } catch (error) {
      console.error('Failed to load skills:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSkillClick = (skill: UserSkillView) => {
    setSelectedItem({ item: skill, type: 'skill' });
    setDrawerOpen(true);
  };

  const handleStrengthClick = (strength: Strength) => {
    setSelectedItem({ item: strength, type: 'strength' });
    setDrawerOpen(true);
  };

  const handleAddSkill = async (skillData: { skillName: string; proficiency: ProficiencyLevel }) => {
    if (!subjectUserId) return;
    try {
      const input: UserSkillInput = {
        id: 0,
        userId: subjectUserId,
        skillName: skillData.skillName,
        proficiency: skillData.proficiency
      };
      const result = await addUserSkill(input);
      if (result.status === 200) {
        await loadSkills();
      }
      setShowAddSkillModal(false);
    } catch (error) {
      console.error('Failed to add skill:', error);
    }
  };

  const handleSuggestSkill = async (skillData: { skillName: string; proficiency: ProficiencyLevel }) => {
    if (!subjectUserId) return;
    try {
      const input: UserSkillInput = {
        id: 0,
        userId: subjectUserId,
        skillName: skillData.skillName,
        proficiency: skillData.proficiency
      };
      const result = await addUserSkill(input);
      if (result.status === 200) {
        await loadSkills();
      }
      setShowSuggestSkillModal(false);
    } catch (error) {
      console.error('Failed to suggest skill:', error);
    }
  };

  const handleUpdateProficiency = async (skillId: number, newProficiency: ProficiencyLevel) => {
    if (!subjectUserId) return;
    try {
      const skill = skills.find(s => s.id === skillId);
      if (!skill) return;

      const input: UserSkillInput = {
        id: skillId,
        userId: subjectUserId,
        skillName: skill.skillName,
        proficiency: newProficiency
      };
      const result = await editUserSkill(skillId, input);
      if (result.status === 200) {
        await loadSkills();
      }
    } catch (error) {
      console.error('Failed to update skill proficiency:', error);
    }
  };

  return (
    <div className="flex flex-col">
      <UserProfile>
        <div className="flex-1 p-2">
          {/* Capabilities Content */}
          <div className="bg-card rounded-lg shadow-sm border border-border">
            <div className="p-6">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-foreground mb-2">{t('Capabilities')}</h2>
                  <p className="text-sm text-muted-foreground">
                    {t('Skills represent capabilities that can be developed and measured over time')}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <div className="flex flex-row justify-end gap-1 mb-0 md:mb-2">
                    {userRole === 'manager' ? (
                      <Button
                        size="sm"
                        onClick={() => setShowAddSkillModal(true)}
                      >
                        <AppIcon name="Plus" className="me-2 h-4 w-4" />
                        {t('Add Skill')}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowSuggestSkillModal(true)}
                      >
                        <AppIcon name="Lightbulb" className="me-2 h-4 w-4" />
                        {t('Suggest Skill')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Skills Section */}
              <SkillsSection
                skills={skills}
                userRole={userRole}
                onSkillClick={handleSkillClick}
                onUpdateProficiency={handleUpdateProficiency}
              />

              {/* Divider */}
              <div className="my-8 border-t border-border" />

              {/* Strengths Section */}
              <StrengthsSection
                strengths={strengths}
                userRole={userRole}
                onStrengthClick={handleStrengthClick}
              />
            </div>
          </div>
        </div>

        {/* Modals */}
        {showAddSkillModal && (
          <AddSkillModal
            isOpen={showAddSkillModal}
            onClose={() => setShowAddSkillModal(false)}
            onSubmit={handleAddSkill}
          />
        )}
        {showSuggestSkillModal && (
          <SuggestSkillModal
            isOpen={showSuggestSkillModal}
            onClose={() => setShowSuggestSkillModal(false)}
            onSubmit={handleSuggestSkill}
          />
        )}

        {/* Skill/Strength Detail Drawer */}
        <SkillStrengthDetailDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          item={selectedItem.item}
          type={selectedItem.type}
        />
      </UserProfile>
    </div>
  );
};

export default Skills;
